import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  approveReviewItem,
  getBousalaReviewState,
  unapproveReviewItem,
  approveAllReviewItems,
} from "@/lib/bousala/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle } from "lucide-react";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const reviewQ = () =>
  queryOptions({ queryKey: ["admin", "bousala", "review"], queryFn: () => getBousalaReviewState() });

export const Route = createFileRoute("/_authenticated/admin/bousala/review")({
  loader: ({ context }) => context.queryClient.ensureQueryData(reviewQ()),
  head: () => ({
    meta: [{ title: "مراجعة بوصلة د. سارة" }, { name: "robots", content: "noindex" }],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: ReviewPage,
});

function ReviewPage() {
  const review = useQuery(reviewQ());
  const qc = useQueryClient();
  const [tab, setTab] = useState<string>("items");

  const fnApprove = useServerFn(approveReviewItem);
  const fnUnapprove = useServerFn(unapproveReviewItem);
  const fnApproveAll = useServerFn(approveAllReviewItems);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["admin", "bousala", "review"] });
  };

  const approve = useMutation({
    mutationFn: (v: { section: string; item_id: string; hash: string }) => fnApprove({ data: v }),
    onSuccess: invalidateAll,
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل"),
  });
  const unapprove = useMutation({
    mutationFn: (v: { section: string; item_id: string }) => fnUnapprove({ data: v }),
    onSuccess: invalidateAll,
  });
  const approveAll = useMutation({
    mutationFn: () => fnApproveAll(),
    onSuccess: () => {
      toast.success("تم اعتماد جميع البنود");
      invalidateAll();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل الاعتماد الشامل"),
  });

  if (review.isLoading || !review.data)
    return <p className="p-6 text-muted-foreground">تحميل...</p>;
  const data = review.data;
  const currentSection = data.sections.find((s) => s.id === tab) ?? data.sections[0];

  return (
    <div className="space-y-6" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">بوصلة د. سارة</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold">مراجعة قبل النشر</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            الحالة الحالية:{" "}
            <Badge variant={data.status === "live" ? "default" : "secondary"}>
              {data.status === "live" ? "منشور" : data.status}
            </Badge>
            {data.published_at ? (
              <span className="ms-2 text-xs">
                (نُشر في {new Date(data.published_at).toLocaleDateString("ar-u-nu-latn")})
              </span>
            ) : null}
          </p>
        </div>
        <Button
          onClick={() => approveAll.mutate()}
          disabled={approveAll.isPending}
        >
          <CheckCircle2 className="me-2 h-4 w-4" />
          {approveAll.isPending ? "جارٍ الاعتماد..." : "اعتمد كل البنود"}
        </Button>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {data.totals.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              tab === t.id ? "bg-primary text-primary-foreground" : "border border-input hover:bg-accent/15"
            }`}
          >
            {t.label} · {t.approved}/{t.total}
          </button>
        ))}
      </div>

      {/* Tab progress + items */}
      {currentSection && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{currentSection.label}</CardTitle>
            <Progress
              value={
                (currentSection.items.filter((i: any) => i.approved).length / Math.max(currentSection.items.length, 1)) *
                100
              }
              className="mt-2 h-2"
            />
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {currentSection.items.map((it: any) => (
                <li key={it.id} className="rounded-xl border border-border bg-background/60 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-muted-foreground">{it.label}</p>
                      <p className="mt-1 text-sm leading-relaxed">{it.preview}</p>
                      {it.stale ? (
                        <p className="mt-1 text-xs text-modern-orange">تم تعديل النص — أعد الاعتماد</p>
                      ) : null}
                    </div>
                    {it.approved ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unapprove.mutate({ section: currentSection.id, item_id: it.id })}
                      >
                        <CheckCircle2 className="me-1 h-4 w-4 text-soft-green" />
                        معتمد
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() =>
                          approve.mutate({ section: currentSection.id, item_id: it.id, hash: it.hash })
                        }
                      >
                        <Circle className="me-1 h-4 w-4" />
                        اعتمد
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}